/**
 * @file useOrderCreateForm.ts
 * @description Состояние формы создания заказа: состав, способ получения,
 * оплата, отправка (phase19.24 шаг 5.1).
 *
 * Страница осталась раскладкой из четырёх карточек — решения живут здесь.
 * Способ получения и условия оплаты связаны правилом F31 («ТК — только
 * предоплата»), и держать эту связь в разметке значило бы дублировать реестр
 * `lib/receipt-method.ts` в JSX.
 *
 * Отправка — три исхода одной кнопки: письмо клиенту, черновик без письма и
 * согласование по телефону (у клиента нет e-mail, ссылку диктуем). Решение
 * пользователя 2026-09-21 (§7 п.3): авто-отправку без e-mail блокируем, а не
 * «отправляем в никуда».
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { useOrderCustomerPicker, type OrderCustomerPickerState } from "./useOrderCustomerPicker";
import { useShopPickupLocation, type ShopPickupLocationState } from "./useShopPickupLocation";
import { ApiError } from "../lib/api-client";
import { listDealItems } from "../lib/api-crm";
import {
  createOrder,
  type CreatedOrderDto,
  type OrderPaymentTerms,
  type OrderSkuDto,
} from "../lib/api-orders";
import {
  addManyRows,
  emptyRow,
  filledRows,
  pickSkuInRow,
  rowsTotal,
  validateRows,
  type OrderCreateRow,
} from "../lib/order-create-rows";
import { skuCountUnit } from "../lib/qty-input";
import {
  buildReceiptPayload,
  defaultPaymentTerms,
  emptyReceiptForm,
  isPaymentTermsAllowed,
  receiptDeliveryAmount,
  validateReceiptForm,
  type ReceiptFormValue,
  type ReceiptIssue,
} from "../lib/receipt-method";

/** Что делает главная кнопка и пункты меню «⋯». */
export type OrderCreateSubmitMode = "send" | "draft" | "phone";

/** Серверные отказы шага 5.1 — человеческим языком, а не кодом. */
const SUBMIT_ERROR_TEXT: Record<string, string> = {
  carrier_requires_prepay:
    "Доставка ТК идёт только по предоплате — поменяйте условия оплаты (F31).",
  pickup_location_missing:
    "У склада магазина нет адреса: заполните пункт самовывоза в настройках складов.",
  customer_email_missing:
    "У клиента нет e-mail: добавьте его в карточке клиента или согласуйте по телефону.",
};

export interface OrderCreateFormState {
  customer: OrderCustomerPickerState;
  rows: OrderCreateRow[];
  addEmptyRow: () => void;
  pickSku: (index: number, sku: OrderSkuDto) => void;
  addManyFromCatalog: (skus: OrderSkuDto[]) => void;
  removeRow: (index: number) => void;
  patchRow: (index: number, patch: Partial<OrderCreateRow>) => void;
  notes: string;
  setNotes: (value: string) => void;
  receipt: ReceiptFormValue;
  setReceipt: (next: ReceiptFormValue) => void;
  paymentTerms: OrderPaymentTerms;
  setPaymentTerms: (next: OrderPaymentTerms) => void;
  pickup: ShopPickupLocationState;
  /** Проблема способа получения — подпись под нужным полем. */
  issue: ReceiptIssue | null;
  /** Общая ошибка формы (клиент, состав, ответ сервера). */
  error: string | null;
  saving: boolean;
  goodsTotal: number;
  /** Стоимость доставки; `null` — доставка не выставляется. */
  deliveryAmount: number | null;
  submit: (mode: OrderCreateSubmitMode) => Promise<CreatedOrderDto | null>;
}

interface Options {
  dealId: string | null;
  preCustomerId: string | null;
}

export function useOrderCreateForm({
  dealId,
  preCustomerId,
}: Options): OrderCreateFormState {
  const customer = useOrderCustomerPicker(preCustomerId);
  const pickup = useShopPickupLocation();
  const [rows, setRows] = useState<OrderCreateRow[]>([]);
  const [notes, setNotes] = useState("");
  const [receipt, setReceiptValue] = useState<ReceiptFormValue>(() => emptyReceiptForm());
  const [paymentTerms, setPaymentTerms] = useState<OrderPaymentTerms>(() =>
    defaultPaymentTerms("PICKUP"),
  );
  const [issue, setIssue] = useState<ReceiptIssue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Позиции из сделки подставляются один раз: дальше состав ведёт менеджер.
  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    void listDealItems(dealId)
      .then((items) => {
        if (cancelled) return;
        const fromDeal = items
          .filter((it) => it.skuId)
          .map((it) => ({
            skuId: it.skuId as string,
            skuCode: it.skuCode ?? "",
            productName: it.productName,
            quantity: it.quantity,
            priceGross: it.priceGross,
            countUnit: skuCountUnit(it),
          }));
        setRows((prev) => (prev.length > 0 ? prev : fromDeal));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  /**
   * Смена исхода тянет за собой условия оплаты: «при получении» на доставке
   * ТК запрещено, и молчаливый запрещённый выбор дал бы 422 при отправке.
   */
  const setReceipt = useCallback((next: ReceiptFormValue) => {
    setReceiptValue(next);
    setIssue(null);
    setPaymentTerms((terms) =>
      isPaymentTermsAllowed(next.method, terms) ? terms : defaultPaymentTerms(next.method),
    );
  }, []);

  const goodsTotal = useMemo(() => rowsTotal(rows), [rows]);
  const picked = customer.customer;

  const submit = useCallback(
    async (mode: OrderCreateSubmitMode): Promise<CreatedOrderDto | null> => {
      setError(null);
      setIssue(null);
      if (!picked) {
        setError("Выберите клиента.");
        return null;
      }
      const items = filledRows(rows);
      if (items.length === 0) {
        setError("Добавьте хотя бы одну позицию.");
        return null;
      }
      const badQty = validateRows(rows);
      if (badQty) {
        setError(badQty);
        return null;
      }
      // Черновик создаём и с незаполненным пунктом самовывоза: письмо не
      // уходит, условия менеджер дособерёт в карточке (§2 «Состояния»).
      const problem = validateReceiptForm(receipt, {
        pickupReady: pickup.ready,
        sending: mode !== "draft",
      });
      if (problem) {
        setIssue(problem);
        return null;
      }

      setSaving(true);
      try {
        return await createOrder({
          customerId: picked.id,
          dealId: dealId ?? undefined,
          items: items.map((r) => ({
            skuId: r.skuId,
            quantity: r.quantity,
            priceGross: r.priceGross || undefined,
          })),
          notes: notes.trim() || undefined,
          receipt: buildReceiptPayload(receipt),
          paymentTerms,
          sendForAgreement: mode === "send",
          agreeByPhone: mode === "phone",
        });
      } catch (err) {
        const code = err instanceof ApiError ? err.code : null;
        const text = (code ? SUBMIT_ERROR_TEXT[code] : null) ?? null;
        setError(text ?? (err instanceof ApiError ? err.message : "Не удалось создать заказ"));
        // Отказ по пункту самовывоза показываем и у самого поля: менеджер
        // ищет причину там, где принимал решение.
        if (code === "pickup_location_missing" && text) {
          setIssue({ field: "pickup", message: text });
        }
        return null;
      } finally {
        setSaving(false);
      }
    },
    [dealId, notes, paymentTerms, picked, pickup.ready, receipt, rows],
  );

  return {
    customer,
    rows,
    addEmptyRow: () => setRows((prev) => [...prev, emptyRow()]),
    pickSku: (index, sku) => setRows((prev) => pickSkuInRow(prev, index, sku)),
    addManyFromCatalog: (skus) => setRows((prev) => addManyRows(prev, skus)),
    removeRow: (index) => setRows((prev) => prev.filter((_, i) => i !== index)),
    patchRow: (index, patch) =>
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r))),
    notes,
    setNotes,
    receipt,
    setReceipt,
    paymentTerms,
    setPaymentTerms,
    pickup,
    issue,
    error,
    saving,
    goodsTotal,
    deliveryAmount: receiptDeliveryAmount(receipt),
    submit,
  };
}
